import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEye } from "react-icons/fa"; // Import the edit icon
import axios from "axios";
import Grid from "../components/Grid"; // Import the Grid component
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";


// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ProductBatch({ onAddProductBatchClick }) {
  const [productionbatch, setProductionBatch] = useState([]);
  const [selectedProductionBatch, setSelectedProductionBatch] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSearchField, setSelectedSearchField] = useState("description"); // New state for search field
  const [sort] = useState({ field: "description", direction: "asc" });
  const [userRole, setUserRole] = useState(""); // State to store user role
  const [organizations, setOrganizations] = useState([]); // List of organizations for Main Admin
  const [selectedOrganization, setSelectedOrganization] = useState(""); // Selected organization for filtering

  // Fetch production batches and locations
  useEffect(() => {
    // Fetch the logged-in user's role and organization data
    const fetchUserRoleAndOrganizations = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        // Fetch user role
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUserRole(userResponse.data.role);

        // Fetch organizations if the user is a Main Admin
        if (userResponse.data.role === "Main Admin") {
          const orgResponse = await axios.get(`${API_BASE_URL}/organizations`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setOrganizations(orgResponse.data.organizations || []);
        }
      } catch (error) {
        console.error("Error fetching user role or organizations:", error);
        toast.error("Error fetching data");
      }
    };

    const fetchProductionBatch = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/products/production-batch`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const productionbatchWithId = response.data.productionBatches.map(
          (batch) => ({
            ...batch,
            id: batch.productionBatchCode,
          })
        );
        setProductionBatch(productionbatchWithId);
      } catch (error) {
        console.error("Error fetching production batches:", error);
        toast.error("Error fetching production batches.");
      }
    };

    fetchUserRoleAndOrganizations();
    fetchProductionBatch();
  }, [userRole, selectedOrganization]);

  useEffect(() => {
    if (selectedProductionBatch) {
      document.body.style.overflow = "hidden"; // Disable background scroll
    } else {
      document.body.style.overflow = "auto"; // Re-enable background scroll
    }

    return () => {
      document.body.style.overflow = "auto"; // Reset on unmount
    };
  }, [selectedProductionBatch]);

  const handleProductionBatchClick = (productionBatch) => {
    console.log("Clicked Production Batch:", productionBatch); // Log the entire production batch

    // Check if selectedProducts is an array
    if (!Array.isArray(productionBatch.selectedProducts)) {
      console.error(
        "Selected Products is not an array:",
        productionBatch.selectedProducts
      );
    }

    const formattedProducts = Array.isArray(productionBatch.selectedProducts)
      ? productionBatch.selectedProducts.map((product) => ({
          id: product.product._id, // Ensure this matches the product ObjectId
          product_name: product.product.product_name, // Access populated product_name
          product_image: product.product.product_image,
          product_code: product.product.product_code, // Access populated product_code
          quantity: product.in_stock_quantity || 0, // Ensure quantity field exists
          price: product.price ? parseFloat(product.price) : 0, // Convert price to a number
          unit:product.product.unit?.unit_name,
        }))
      : []; // Fallback to an empty array if products is undefined

    setSelectedProductionBatch({
      ...productionBatch,
      products: formattedProducts, // Format the products properly
      location: productionBatch.location?.location_name || "",
      organization: productionBatch.organization?.organization_name || "",
      status: productionBatch.status || "Active",
    });
  };

  const handleClosePopup = () => {
    setSelectedProductionBatch(null);
  };

  const handleDownloadExcel = async () => {
  const dataToExport = searchTerm.trim().length > 0 ? filteredProductionBatches : productionbatch;

    if (dataToExport.length === 0) {
      toast.warn("No production batch data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Production Batches");

    // Define header
    const header = [
      "Production Batch Code",
      "Description",
      "Location",
      "Created Date",
      "Created By",
    ];
    worksheet.addRow(header);

    // Style header
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" },
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add data rows
    dataToExport.forEach((batch) => {
      const products = Array.isArray(batch.selectedProducts)
        ? batch.selectedProducts
            .map((p) => {
              const product = p.product || {};
              return `${product.product_code || "N/A"} - ${product.product_name || "N/A"} | Qty: ${p.in_stock_quantity || 0} | Price: ${p.price || 0} | Unit: ${product.unit?.unit_name || "N/A"}`;
            })
            .join("\n")
        : "N/A";

      worksheet.addRow([
        batch.productionBatchCode || "N/A",
        batch.description || "N/A",
        batch.location?.location_name || "N/A",
        batch.createdDate ? new Date(batch.createdDate).toLocaleDateString("en-GB") : "N/A",
        batch.createdBy?.username || "N/A",
      ]);
    });

    worksheet.columns.forEach((column) => {
      column.width = 30;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "product_batches.xlsx");
  };

  // Filter and sort the production batches
  const filteredProductionBatches = productionbatch
  .filter((batch) => {
    switch (selectedSearchField) {
      case "description":
        return (
          batch.description &&
          batch.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      case "createdBy":
        return (
          batch.createdBy.username &&
          batch.createdBy.username
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      case "productionBatchCode":
        return (
          batch.productionBatchCode &&
          batch.productionBatchCode
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      case "organization":
        return batch.organization
          ? batch.organization.organization_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          : false;
      case "location":
        return batch.location
          ? batch.location.location_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          : false;
      case "Date":
        // Assuming createdDate is in ISO format, we can compare it directly
        const searchDate = new Date(searchTerm);
        return (
          batch.createdDate &&
          new Date(batch.createdDate).toLocaleDateString() ===
          searchDate.toLocaleDateString()
        );
      default:
        return false;
    }
  })
  .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)); // Sort by date in descending order


  const columns = [
    { field: "id", headerName: "ID", flex: 1 }, // Use id here
    {
      field: "createdDate",
      headerName: "Created Date",
      flex: 1,
      renderCell: (params) => (
        <span>
          {params.value ? new Date(params.value).toLocaleDateString("en-GB") : "N/A"}
        </span>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      renderCell: (params) => (
        <span>
          {params.row.location ? params.row.location.location_name : "N/A"}
        </span>
      ),
    },
    ...(userRole === "Main Admin"
      ? [
          {
            field: "organization",
            headerName: "Organization",
            flex: 1,
            renderCell: (params) => (
              <span>
                {params.row.organization
                  ? params.row.organization.organization_name
                  : "N/A"}
              </span>
            ),
          },
        ]
      : []),
      {
        field: "createdBy",
        headerName: "Created By",
        flex: 1,
        renderCell: (params) => (
          <span>
            {params.row.createdBy ? params.row.createdBy.username : "N/A"}
          </span>
        ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <div className="flex  items-center w-full h-full">
          <button
            onClick={() => handleProductionBatchClick(params.row)}
            className="bg-yellow-500 text-white p-2  flex items-center"
            style={{ width: "30px", height: "30px" }} // Set fixed size for the icon button
          >
            <FaEye className="text-white" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen">
      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Production Batch</h1>
          {/* Conditionally render the Add Production Batch button */}
          {(userRole === "Company Admin" || userRole === "Farm User") && (
            <button
              onClick={onAddProductBatchClick}
              className="bg-green-700 text-white px-4 py-2 rounded-lg"
            >
              Add Production Batch
            </button>
          )}
        </div>
        {userRole === "Main Admin" && (
          <div className="mb-4">
            <select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Organizations</option>
              {organizations.map((org) => (
                <option key={org._id} value={org._id}>
                  {org.organization_name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="bg-white p-4 rounded-lg shadow mb-8">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="description">Description</option>
                <option value="createdBy">Created By</option>
                <option value="location">Location</option>
                <option value="productionBatchCode">Batch Code</option>
                <option value="Date">Date</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Batch here"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>          

          {/* Product Grid */}
          <Grid rows={filteredProductionBatches} columns={columns} getRowId={(row) => row.productionBatchCode}/>
        </div>

        {/* Popup for Production Batch Details */}
        {selectedProductionBatch && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
            style={{ overflowY: "auto" }}
          >
            <div
              className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto"
              style={{ maxHeight: "90vh", overflowY: "auto" }}
            >
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <div className="w-full">
                <h2 className="text-xl font-semibold mb-4">
                  {selectedProductionBatch.productionBatchCode}
                </h2>

                {/* Production Batch Description */}
                <div className="mb-4">
                  <label className="block text-gray-700">
                    Production Batch Description
                  </label>
                  <input
                    type="text"
                    value={selectedProductionBatch.description}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    readOnly
                  />
                </div>

                {/* PB Location */}
                <div className="mb-4">
                  <label className="block text-gray-700">PB Location</label>
                  <input
                    type="text"
                    value={selectedProductionBatch.location}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    readOnly
                  />
                </div>

                {/* Production Batch Created User */}
                <div className="mb-4">
                  <label className="block text-gray-700">
                    Production Batch Created User
                  </label>
                  <input
                    type="text"
                    value={selectedProductionBatch.createdBy.username}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    readOnly
                  />
                </div>

                {/* Production Batch Created Date */}
                <div className="mb-4">
                  <label className="block text-gray-700">
                    Production Batch Created Date
                  </label>
                  <input
                    type="text"
                    value={selectedProductionBatch.createdDate}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    readOnly
                  />
                </div>

                {/* Selected Products Grid */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold mb-2">
                    Production Batch Selected Products
                  </h3>
                  <div style={{ height: 400, width: "100%" }}>
                    <DataGrid
                      rows={selectedProductionBatch?.products || []} // Use the products from selectedProductionBatch
                      columns={[
                        { field: "product_code", headerName: "ID", width: 100, flex: 0.6, },
                        {
                          field: "product_image",
                          headerName: "Image",
                          width: 150,
                          flex: 1,
                          renderCell: (params) =>
                            params.row.product_image ? (
                              <img
                                src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                                alt="Product"
                                className="h-16 w-16 object-cover rounded-md"
                              />
                            ) : (
                              "No Image"
                            ),
                        },
                        {
                          field: "product_name",
                          headerName: "Product Name",
                          width: 150,
                          flex: 1,
                        },
                        {
                          field: "quantity",
                          headerName: "Quantity",
                          width: 150,
                          headerAlign: "right",
                          align: "right",
                          flex: 1,
                        },
                        { 
                          field: "unit", 
                          headerName: "Units", 
                          flex: 1, 
                          headerAlign: "center", 
                          align: "center" 
                        },
                        {
                          field: "price",
                          headerName: "Price (LKR)",
                          width: 150,
                          flex: 1,
                          headerAlign: "right",
                          align: "right",
                          renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}`
                        },
                      ]}
                      pageSize={5}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductBatch;
